/**
 * Preview Cache Utility
 * Implements LRU cache with request deduplication for recipe previews
 */

const MAX_CACHE_SIZE = 50; // Maximum number of cached previews
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

class PreviewCache {
  constructor() {
    this.cache = new Map(); // URL -> { recipe, timestamp, accessCount }
    this.pendingRequests = new Map(); // URL -> Promise
  }

  /**
   * Get cached preview or return null
   */
  get(url) {
    const entry = this.cache.get(url);
    if (!entry) return null;

    // Check if expired
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      this.cache.delete(url);
      return null;
    }

    // Update access count and move to end (LRU)
    entry.accessCount++;
    this.cache.delete(url);
    this.cache.set(url, entry);

    return entry.recipe;
  }

  /**
   * Set cached preview
   */
  set(url, recipe) {
    // If cache is full, remove least recently used
    if (this.cache.size >= MAX_CACHE_SIZE && !this.cache.has(url)) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(url, {
      recipe,
      timestamp: Date.now(),
      accessCount: 1,
    });
  }

  /**
   * Check if a request is pending for this URL
   */
  getPendingRequest(url) {
    return this.pendingRequests.get(url);
  }

  /**
   * Set a pending request promise
   */
  setPendingRequest(url, promise) {
    this.pendingRequests.set(url, promise);
    
    // Clean up after request completes
    promise
      .then(() => {
        this.pendingRequests.delete(url);
      })
      .catch(() => {
        this.pendingRequests.delete(url);
      });
  }

  /**
   * Clear expired entries
   */
  clearExpired() {
    const now = Date.now();
    for (const [url, entry] of this.cache.entries()) {
      if (now - entry.timestamp > CACHE_TTL) {
        this.cache.delete(url);
      }
    }
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: MAX_CACHE_SIZE,
      pendingRequests: this.pendingRequests.size,
    };
  }
}

// Singleton instance
const previewCache = new PreviewCache();

// Clean up expired entries periodically
setInterval(() => {
  previewCache.clearExpired();
}, 5 * 60 * 1000); // Every 5 minutes

export default previewCache;












